<template>
  <VueSignaturePad
    ref="pad"
    :width="width"
    :height="height"
    :options="padOptions"
    :custom-style="customStyle"
  />
</template>

<script>
/**
 * Wspólne pole podpisu panelu.
 *
 * Jedyne miejsce, przez które przechodzi pozyskanie podpisu. Dzięki temu
 * zmiana źródła podpisu sprowadza się do zmiany w tym pliku, a nie w
 * kilkunastu miejscach panelu.
 *
 * Źródła podpisu:
 *  - dziś: canvas — mysz, palec na ekranie dotykowym, a także tablet
 *    graficzny lub pen display, które system widzi jako zwykły wskaźnik
 *    (działają bez żadnych zmian w kodzie);
 *  - w planach: pad sprzętowy z własnym ekranem (np. Wacom STU) — wymaga
 *    mostka lokalnego i wpięcia się w metodę `capture()`, patrz komentarz
 *    przy niej.
 *
 * Komponent jest podmianką 1:1 za <VueSignaturePad>: wystawia te same metody
 * (saveSignature, clearSignature, undoSignature, resizeCanvas, isEmpty,
 * fromDataURL, ...) i renderuje ten sam element, więc klasy CSS i style
 * przekazane ze strony działają tak samo. Istniejący kod stron nie wymaga
 * zmian poza podmianą nazwy znacznika.
 *
 * Nowy kod powinien korzystać z `grab()` oraz z v-model zamiast sięgać po
 * `saveSignature()` i ręcznie sprawdzać `isEmpty`.
 */
export default {
  name: "SignatureField",

  props: {
    /** v-model — podpis jako data URL (base64) albo null, gdy pole jest puste. */
    value: { type: String, default: null },
    width: { type: String, default: "100%" },
    height: { type: String, default: "200px" },
    /** Opcje signature_pad. Własne `onEnd` nadal jest wywoływane. */
    options: { type: Object, default: () => ({}) },
    customStyle: { type: Object, default: () => ({}) },
    /** Format zapisu: image/png (domyślnie), image/jpeg albo image/svg. */
    imageType: { type: String, default: "image/png" },
    /** Blokuje rysowanie — do podglądu już złożonego podpisu. */
    disabled: { type: Boolean, default: false },
  },

  computed: {
    padOptions() {
      return { ...this.touchTuning, ...this.options, onEnd: this.handleEnd };
    },
  },

  watch: {
    disabled(locked) {
      this.applyLock(locked);
    },
    value(next) {
      // Pomijamy odbicie własnego emitu, żeby nie przerysowywać pola w kółko.
      if (next === this.lastEmitted) return;
      if (next) this.setImage(next, false);
      else this.clearSignature(false);
    },
  },

  created() {
    // Ostatnia wartość wysłana przez `input` — służy tylko do odsiania echa
    // z v-model, więc celowo poza `data` (nie musi być reaktywna).
    this.lastEmitted = null;
    // Na urządzeniach dotykowych (tablet z rysikiem, telefon) domyślna kreska
    // signature_pad (0.5–2.5) wygląda na dużym ekranie jak drucik, a throttle
    // 16 ms gubi próbki szybkich rysików (ekrany 90–144 Hz). Grubsza linia
    // i pełne próbkowanie dają podpis jak z długopisu. Strony mogą to nadal
    // nadpisać własnymi `options`; na desktopie z myszką nic się nie zmienia.
    const coarse = typeof window !== "undefined"
      && window.matchMedia
      && window.matchMedia("(any-pointer: coarse)").matches;
    this.touchTuning = coarse
      ? { minWidth: 1.1, maxWidth: 3.2, throttle: 0, minDistance: 1 }
      : {};
  },

  mounted() {
    if (this.disabled) this.applyLock(true);
    if (this.value) this.setImage(this.value, false);
  },

  methods: {
    /**
     * Jedyna ścieżka pozyskania podpisu — przechodzą przez nią zarówno
     * `grab()`, jak i zgodnościowe `saveSignature()`.
     *
     * Tutaj wepnie się obsługa pada sprzętowego: przed odczytem canvasu
     * sprawdzimy, czy dostępny jest mostek do urządzenia, i jeśli tak —
     * pobierzemy podpis z niego. Zmiana w tym jednym miejscu obejmie
     * wszystkie pola podpisu w panelu.
     */
    capture(type = this.imageType, encoderOptions) {
      const pad = this.$refs.pad;
      if (!pad || !pad.saveSignature) return { isEmpty: true, data: undefined };
      return pad.saveSignature(type, encoderOptions);
    },

    /**
     * Zdejmuje podpis z pola — zwraca data URL albo null, gdy nie podpisano.
     * Nigdy nie rzuca wyjątkiem; przy błędzie zwraca null i loguje ostrzeżenie.
     * Docelowe API pola podpisu.
     */
    grab() {
      try {
        const res = this.capture();
        return res && !res.isEmpty ? res.data : null;
      } catch (e) {
        console.warn("nie udało się pobrać podpisu", e);
        return null;
      }
    },

    /** Czy pole jest puste. Bezpieczne przed zamontowaniem pada. */
    isEmpty() {
      const pad = this.$refs.pad;
      if (!pad || !pad.isEmpty) return true;
      try {
        return pad.isEmpty();
      } catch (e) {
        return true;
      }
    },

    /** Wstawia gotowy obraz podpisu (np. faksymilkę) do pola. */
    setImage(dataUrl, emit = true) {
      const pad = this.$refs.pad;
      if (!pad || !pad.fromDataURL || !dataUrl) return;
      try {
        pad.fromDataURL(dataUrl);
        if (emit) this.emitValue(dataUrl);
      } catch (e) {
        console.warn("nie udało się wstawić obrazu podpisu", e);
      }
    },

    // ─── API zgodne z VueSignaturePad ────────────────────────────────────
    // Zachowane, żeby komponent był podmianką 1:1 w istniejącym kodzie.

    saveSignature(type = this.imageType, encoderOptions) {
      return this.capture(type, encoderOptions);
    },

    clearSignature(emit = true) {
      const pad = this.$refs.pad;
      if (pad && pad.clearSignature) pad.clearSignature();
      if (emit) this.emitValue(null);
    },

    undoSignature() {
      const pad = this.$refs.pad;
      if (pad && pad.undoSignature) pad.undoSignature();
      this.emitValue(this.grab());
    },

    resizeCanvas() {
      const pad = this.$refs.pad;
      if (pad && pad.resizeCanvas) pad.resizeCanvas();
    },

    fromDataURL(dataUrl, options, callback) {
      const pad = this.$refs.pad;
      if (pad && pad.fromDataURL) return pad.fromDataURL(dataUrl, options, callback);
    },

    toData() {
      const pad = this.$refs.pad;
      return pad && pad.toData ? pad.toData() : [];
    },

    lockSignaturePad() {
      this.applyLock(true);
    },

    openSignaturePad() {
      this.applyLock(false);
    },

    // ─── Wewnętrzne ──────────────────────────────────────────────────────

    /** Po zakończeniu kreski aktualizujemy v-model i oddajemy sterowanie stronie. */
    handleEnd(...args) {
      this.emitValue(this.grab());
      if (typeof this.options.onEnd === "function") this.options.onEnd(...args);
    },

    emitValue(dataUrl) {
      this.lastEmitted = dataUrl;
      this.$emit("input", dataUrl);
    },

    applyLock(locked) {
      const pad = this.$refs.pad;
      if (!pad) return;
      if (locked && pad.lockSignaturePad) pad.lockSignaturePad();
      if (!locked && pad.openSignaturePad) pad.openSignaturePad();
    },
  },
};
</script>
